import type { UTCDate } from '@date-fns/utc';
import { clamp } from 'date-fns';

import type {
  FilterConfig,
  FilterDefinition,
  FilterHandler,
} from '../engine/types';
import { type DateRangeFilter, FilterId, FilterType } from '../types/filters';

/**
 * Posting Discovery "Post Date" filter. Reuses the DATE_RANGE filter type +
 * DateRangeFilterForm, but is independent of the analysis date axis: it stays
 * inactive until the user picks a range, never applies a template preset
 * default, and always clamps to day-resolution (week) bounds so the picker is
 * day-level regardless of the page's metric-derived granularity.
 */
export const postDateDefinition: FilterDefinition<DateRangeFilter> = {
  id: FilterId.POST_DATE,
  name: 'Post Date',
  min: null,
  max: null,
  type: FilterType.DATE_RANGE,
  isActive: true,
};

export const postDateHandler: FilterHandler<DateRangeFilter> = {
  isActive(currentFilterState: DateRangeFilter) {
    return currentFilterState.min !== null || currentFilterState.max !== null;
  },
  isDefault(
    currentFilterState: DateRangeFilter,
    definition: FilterDefinition<DateRangeFilter>
  ) {
    return (
      currentFilterState.min === definition.min &&
      currentFilterState.max === definition.max
    );
  },
  getEffectiveFilter(
    currentFilterState: DateRangeFilter,
    _definition: FilterDefinition<DateRangeFilter>,
    config: FilterConfig
  ) {
    const bounds = config.availableDateRange?.week;
    const hasValue =
      currentFilterState.min !== null || currentFilterState.max !== null;

    if (!hasValue || !bounds?.min || !bounds?.max) {
      // Inactive until the user submits a range.
      return { ...currentFilterState, isVisible: false };
    }

    const { min: systemMin, max: systemMax } = bounds;
    const clampToBounds = (date: UTCDate): UTCDate =>
      clamp(date, { start: systemMin, end: systemMax }) as UTCDate;

    return {
      ...currentFilterState,
      min: currentFilterState.min
        ? clampToBounds(currentFilterState.min)
        : null,
      max: currentFilterState.max
        ? clampToBounds(currentFilterState.max)
        : null,
      isUserSubmitted: true,
      isVisible: true,
    };
  },
};
